import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImageOff, Layers, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

// Üretim Formu seçimi: Tüm Sipariş veya tek ürün (o ürünün tüm bedenleri) — backend ?product_id=
const groupProducts = (order) => {
  const lines = order?.line_items?.length ? order.line_items
    : (order?.product_id || order?.product_name ? [{ product_id: order.product_id, product_name: order.product_name, quantity: order.quantity }] : []);
  const m = new Map();
  lines.forEach((l) => {
    const k = l.product_id || `name:${l.product_name || ""}`;
    const g = m.get(k) || { key: k, product_id: l.product_id, name: l.product_name, qty: 0, sizes: 0 };
    g.qty += Number(l.quantity) || 0; g.sizes += 1; m.set(k, g);
  });
  return [...m.values()];
};

const Thumb = ({ pid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!pid) return undefined;
    let alive = true;
    api.get(`/products/${pid}/images`).then(({ data }) => {
      const im = (data || []).find((x) => x.is_primary) || (data || [])[0];
      if (!im) return null;
      return api.get(`/products/${pid}/images/${im.id}/thumb`, { responseType: "blob" }).then((r) => alive && setUrl(URL.createObjectURL(r.data)));
    }).catch(() => {});
    return () => { alive = false; };
  }, [pid]);
  return (
    <div className="w-10 h-10 shrink-0 rounded-sm border border-border bg-muted/30 overflow-hidden flex items-center justify-center">
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-muted-foreground" />}
    </div>
  );
};

export function ProductionFormDialog({ order, open, onClose }) {
  const [busy, setBusy] = useState("");
  const products = groupProducts(order);
  const print = async (pid, key) => {
    setBusy(key);
    try {
      const res = await api.get(`/orders/${order.id}/production-pdf`, { responseType: "blob", params: pid ? { product_id: pid } : {} });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (win) win.addEventListener("load", () => setTimeout(() => win.print(), 400));
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const row = "w-full flex items-center gap-3 p-2 rounded-sm border border-border hover:bg-muted/40 text-left disabled:opacity-60";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="production-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="w-4 h-4" /> Üretim Formu</DialogTitle>
          <DialogDescription>Tek ürün seçin (o ürünün tüm bedenleri ve uygulamaları) veya tüm siparişi alın.</DialogDescription>
        </DialogHeader>
        <button type="button" className={`${row} border-primary/50`} disabled={!!busy} onClick={() => print(null, "all")} data-testid="production-form-all">
          <div className="w-10 h-10 shrink-0 rounded-sm bg-primary/10 flex items-center justify-center"><Layers className="w-4 h-4 text-primary" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Tüm Sipariş</div>
            <div className="text-[11px] text-muted-foreground">{products.length} ürün · {products.reduce((a, p) => a + p.qty, 0)} adet — ürün ürün gruplu</div>
          </div>
          {busy === "all" && <Loader2 className="w-4 h-4 animate-spin" />}
        </button>
        <div className="max-h-80 overflow-y-auto space-y-1.5">
          {products.map((p) => (
            <button key={p.key} type="button" className={row} disabled={!!busy || !p.product_id} onClick={() => print(p.product_id, p.key)}
              data-testid={`production-form-product-${p.product_id || p.key}`}>
              <Thumb pid={p.product_id} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{p.name || "—"}</div>
                <div className="text-[11px] text-muted-foreground">{p.sizes} beden/varyant · Toplam {p.qty} adet</div>
              </div>
              {busy === p.key && <Loader2 className="w-4 h-4 animate-spin" />}
            </button>
          ))}
        </div>
        <div className="flex justify-end"><Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="production-form-close">Kapat</Button></div>
      </DialogContent>
    </Dialog>
  );
}
